<template>
  <b-modal
    id="bulk-payment-modal"
    v-model="showModal"
    title="Bulk Payment - Update Selected Payments"
    size="xl"
    dialog-class="modal-xl"
    no-close-on-esc
    no-close-on-backdrop
    hide-footer
  >
    <div v-if="isLoading" class="text-center p-4">
      <b-spinner label="Loading..."></b-spinner>
    </div>

    <div v-else>
      <!-- Apply to All Button -->
      <div class="mb-3 text-right">
        <b-button 
          variant="outline-primary" 
          size="sm"
          @click="showApplyToAll = !showApplyToAll"
        >
          {{ showApplyToAll ? 'Hide Apply Same Values' : 'Apply Same Values to All' }}
        </b-button>
      </div>

      <!-- Apply to All Section -->
      <b-card v-if="showApplyToAll" class="mb-3" style="background-color: #f8f9fa;">
        <h6 class="mb-3">
          <b-badge variant="info">Apply Same Values to All</b-badge>
        </h6>
        <b-row>
          <b-col md="3">
            <b-form-group label="Pay Mode" label-for="apply-pay-mode">
              <b-form-select
                id="apply-pay-mode"
                v-model="applyToAll.inv_pay_mode"
                :options="payModeOptions"
              >
                <option value="">Select Pay Mode</option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Reference No" label-for="apply-ref-no">
              <b-form-input
                id="apply-ref-no"
                v-model.trim="applyToAll.inv_pay_ref_no"
                placeholder="Reference No"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Amount" label-for="apply-amt">
              <b-form-input
                id="apply-amt"
                type="number"
                step="0.01"
                min="0"
                v-model.number="applyToAll.inv_pay_amt"
                placeholder="Amount"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Payment Date" label-for="apply-date">
              <b-form-input
                id="apply-date"
                type="date"
                v-model="applyToAll.inv_pay_date"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12" class="text-right">
            <b-button size="sm" variant="outline-primary" @click="applyAllFieldsToAll">
              Apply All Fields to All Rows
            </b-button>
          </b-col>
        </b-row>
      </b-card>

      <!-- Payment Details Table -->
      <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
        <table class="table table-sm table-bordered table-hover">
          <thead class="thead-light" style="position: sticky; top: 0; z-index: 10; background: #f8f9fa;">
            <tr>
              <th style="width: 50px;">SN</th>
              <th style="min-width: 120px;">Transaction No</th>
              <th style="min-width: 100px;">Job No</th>
              <th style="min-width: 100px;">Type</th>
              <th style="min-width: 120px;">Inv Amount</th>
              <th style="min-width: 120px;">Inv Date</th>
              <th style="min-width: 150px;">Particular</th>
              <th style="min-width: 100px;">Currency</th>
              <th style="min-width: 120px;">Sell Amount</th>
              <th style="min-width: 120px;">Bill To Customer</th>
              <th style="min-width: 150px;">Remark</th>
              <th style="min-width: 120px;">Document</th>
              <th style="min-width: 120px;">
                <div class="d-flex align-items-center">
                  <b-form-checkbox
                    v-model="applyFieldCheckboxes.inv_pay_mode"
                    class="mr-2"
                    @change="(checked) => { if (checked) applyPayModeToAll(); else clearPayModeFromAll(); }"
                  ></b-form-checkbox>
                  Pay Mode
                </div>
              </th>
              <th style="min-width: 150px;">
                <div class="d-flex align-items-center">
                  <b-form-checkbox
                    v-model="applyFieldCheckboxes.inv_pay_ref_no"
                    class="mr-2"
                    @change="(checked) => { if (checked) applyRefNoToAll(); else clearRefNoFromAll(); }"
                  ></b-form-checkbox>
                  Reference No
                </div>
              </th>
              <th style="min-width: 120px;">
                <div class="d-flex align-items-center">
                  <b-form-checkbox
                    v-model="applyFieldCheckboxes.inv_pay_amt"
                    class="mr-2"
                    @change="(checked) => { if (checked) applyAmountToAll(); else clearAmountFromAll(); }"
                  ></b-form-checkbox>
                  Amount
                </div>
              </th>
              <th style="min-width: 120px;">
                <div class="d-flex align-items-center">
                  <b-form-checkbox
                    v-model="applyFieldCheckboxes.inv_pay_date"
                    class="mr-2"
                    @change="(checked) => { if (checked) applyDateToAll(); else clearDateFromAll(); }"
                  ></b-form-checkbox>
                  Payment Date
                </div>
              </th>
              <th style="min-width: 100px;">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in paymentItems" :key="index">
              <td>{{ index + 1 }}</td>
              <td>
                <strong>{{ item.transactionNo || item.TransactionNo || item.ape_transaction_no || '-' }}</strong>
                <br />
                <small v-if="!item.isParent" class="text-muted">Child Item</small>
              </td>
              <td>{{ item.JobNo || item.jobNo || '-' }}</td>
              <td>
                <b-badge :variant="item.PaymentType == 1 ? 'info' : (item.PaymentType == 2 ? 'secondary' : 'light')">
                  {{ item.PaymentType == 1 ? 'Exp' : (item.PaymentType == 2 ? 'Additional Task' : '-') }}
                </b-badge>
              </td>
              <td>{{ item.inv_amt || '-' }}</td>
              <td>{{ item.inv_date ? formatDate(item.inv_date) : '-' }}</td>
              <td>
                {{ item.ledgermaster_relation && item.ledgermaster_relation.ledger 
                  ? item.ledgermaster_relation.ledger 
                  : (item.ledger || item.Particular || '-') }}
              </td>
              <td>
                {{ item.master_currency_relation && item.master_currency_relation.currencyCode 
                  ? item.master_currency_relation.currencyCode 
                  : (item.currency || item.Currency || '-') }}
              </td>
              <td>{{ item.selling_amt || item.sellAmount || '-' }}</td>
              <td>
                <b-badge :variant="item.sc_category == 'Yes' ? 'success' : 'secondary'">
                  {{ item.sc_category || '-' }}
                </b-badge>
              </td>
              <td>{{ item.sc_remark || item.remark || '-' }}</td>
              <td>
                <b-button 
                  v-if="item.inv_doc_url" 
                  size="sm" 
                  variant="warning"
                  class="p-1"
                  style="font-size: 0.7rem; line-height: 1;"
                  :href="getDocumentUrl(item.inv_doc_url)"
                  target="_blank"
                  rel="noopener"
                >
                  View Doc
                </b-button>
                <span v-else>-</span>
              </td>
              <td>
                <b-form-select
                  v-model="item.inv_pay_mode"
                  :options="payModeOptions"
                  size="sm"
                  :state="getFieldState(item, 'inv_pay_mode')"
                >
                  <option value="">Select</option>
                </b-form-select>
              </td>
              <td>
                <b-form-input
                  v-model.trim="item.inv_pay_ref_no"
                  placeholder="Reference No"
                  size="sm"
                  :state="getFieldState(item, 'inv_pay_ref_no')"
                />
              </td>
              <td>
                <b-form-input
                  v-model.number="item.inv_pay_amt"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Amount"
                  size="sm"
                  :state="getFieldState(item, 'inv_pay_amt')"
                />
              </td>
              <td>
                <b-form-input
                  v-model="item.inv_pay_date"
                  type="date"
                  size="sm"
                  :state="getFieldState(item, 'inv_pay_date')"
                />
              </td>
              <td>
                <b-badge :variant="item.InvPayStatus == 1 ? 'success' : 'warning'">
                  {{ item.InvPayStatus == 1 ? 'Paid' : 'Pending' }}
                </b-badge>
              </td>
            </tr>
            <tr v-if="!paymentItems || paymentItems.length === 0">
              <td colspan="16" class="text-center text-muted py-3">
                No payment items selected
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Summary -->
      <div class="mt-3">
        <b-alert variant="info" show>
          <strong>Total Selected:</strong> {{ paymentItems.length }} payment(s)
        </b-alert>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-end mt-3">
        <b-button variant="outline-secondary" class="mr-2" @click="closeModal" :disabled="isSubmitting">
          Cancel
        </b-button>
        <b-button variant="primary" @click="submitBulkPayment" :disabled="isSubmitting || !isFormValid">
          <b-spinner v-if="isSubmitting" small class="mr-2"></b-spinner>
          {{ isSubmitting ? 'Processing...' : 'Save All Payments' }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { URL_BASE_DOC } from "../../helper/constant.js";
import moment from "moment";

export default {
  name: 'BulkPaymentModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    payModeOptions: {
      type: Array,
      default: () => [
        { value: 'VIA VATECH', text: 'VIA VATECH' },
        { value: 'ONLINE', text: 'ONLINE' },
      ],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showModal: false,
      paymentItems: [],
      isSubmitting: false,
      showApplyToAll: false,
      applyFieldCheckboxes: {
        inv_pay_mode: false,
        inv_pay_ref_no: false,
        inv_pay_amt: false,
        inv_pay_date: false,
      },
      applyToAll: {
        inv_pay_mode: '',
        inv_pay_ref_no: '',
        inv_pay_amt: null,
        inv_pay_date: '',
      },
    };
  },
  computed: {
    isFormValid() {
      if (!this.paymentItems || this.paymentItems.length === 0) return false;
      return this.paymentItems.every((item) => {
        return (
          item.inv_pay_mode &&
          item.inv_pay_ref_no &&
          item.inv_pay_amt !== null &&
          Number(item.inv_pay_amt) > 0 &&
          item.inv_pay_date
        );
      });
    },
  },
  watch: {
    show(newVal) {
      this.showModal = newVal;
      if (newVal) {
        // Reset form state when opening
        this.resetForm();
        this.initializePaymentItems();
      }
    },
    showModal(newVal) {
      if (!newVal) {
        // Reset form when closing
        this.resetForm();
        this.$emit('close');
      }
    },
    selectedItems: {
      handler(newVal, oldVal) {
        // Only reinitialize if modal is showing and items actually changed
        if (this.showModal && newVal && newVal.length > 0) {
          // Check if items are actually different
          const newKeys = (newVal || []).map(item => item.key || `${item.TransactionNo}::${item.ApeId}`).sort().join(',');
          const oldKeys = (oldVal || []).map(item => item.key || `${item.TransactionNo}::${item.ApeId}`).sort().join(',');
          if (newKeys !== oldKeys) {
            this.initializePaymentItems();
          }
        } else if (this.showModal && (!newVal || newVal.length === 0)) {
          // Clear items if selection is empty
          this.paymentItems = [];
        }
      },
      deep: true,
    },
  },
  methods: {
    initializePaymentItems() {
      // Transform selected items into editable payment items
      this.paymentItems = (this.selectedItems || []).map((item) => {
        // Handle both parent and child items
        let transactionNo = item.TransactionNo || item.transactionNo || '';
        const parts = (item.key && item.key.split('::')) || [];
        
        // For child items, extract transactionNo from key
        if (!transactionNo && parts.length > 0) {
          transactionNo = parts[0];
        }
        
        // Determine if it's a parent or child
        const isParent = item.isParent !== undefined ? item.isParent : (parts.length < 3);
        
        return Object.assign({}, item, {
          // Payment fields (editable)
          inv_pay_mode: item.inv_pay_mode || '',
          inv_pay_ref_no: item.inv_pay_ref_no || '',
          inv_pay_amt: item.inv_pay_amt !== null && item.inv_pay_amt !== undefined ? Number(item.inv_pay_amt) : null,
          inv_pay_date: item.inv_pay_date || '',
          // Display fields
          transactionNo: transactionNo,
          TransactionNo: transactionNo,
          JobNo: item.JobNo || item.jobNo || '',
          ModuleId: item.ModuleId || item.moduleId || '',
          ApeId: item.ApeId || item.apeId || item.TransactionId || '',
          TransactionId: item.TransactionId || item.ApeId || '',
          InvPayStatus: item.InvPayStatus || item.invPayStatus || 0,
          // For child items
          parentId: parts[1] || item.parentId || '',
          childId: parts[2] || item.childId || item.TransactionId || '',
          isParent: isParent,
          // Store original key for reference
          originalKey: item.key || '',
          // Preserve all other fields from original item (PaymentType, inv_amt, inv_date, etc.)
          PaymentType: item.PaymentType,
          inv_amt: item.inv_amt,
          inv_date: item.inv_date,
          selling_amt: item.selling_amt,
          sc_category: item.sc_category,
          sc_remark: item.sc_remark,
          inv_doc_url: item.inv_doc_url,
          ledgermaster_relation: item.ledgermaster_relation,
          master_currency_relation: item.master_currency_relation,
          ape_transaction_no: item.ape_transaction_no,
        });
      });
    },
    getFieldState(item, field) {
      if (!item[field]) return null;
      if (field === 'inv_pay_amt') {
        return Number(item[field]) > 0 ? true : false;
      }
      return item[field] ? true : null;
    },
    applyPayModeToAll() {
      // Use value from "Apply to All" section if available, otherwise use first row's value
      let valueToApply = this.applyToAll.inv_pay_mode;
      if (!valueToApply && this.paymentItems.length > 0) {
        // Find first row with a value
        const firstRowWithValue = this.paymentItems.find(item => item.inv_pay_mode);
        if (firstRowWithValue) {
          valueToApply = firstRowWithValue.inv_pay_mode;
        }
      }
      if (valueToApply) {
        this.paymentItems.forEach((item) => {
          item.inv_pay_mode = valueToApply;
        });
      }
    },
    applyRefNoToAll() {
      // Use value from "Apply to All" section if available, otherwise use first row's value
      let valueToApply = this.applyToAll.inv_pay_ref_no;
      if (!valueToApply && this.paymentItems.length > 0) {
        // Find first row with a value
        const firstRowWithValue = this.paymentItems.find(item => item.inv_pay_ref_no);
        if (firstRowWithValue) {
          valueToApply = firstRowWithValue.inv_pay_ref_no;
        }
      }
      if (valueToApply) {
        this.paymentItems.forEach((item) => {
          item.inv_pay_ref_no = valueToApply;
        });
      }
    },
    applyAmountToAll() {
      // Use value from "Apply to All" section if available, otherwise use first row's value
      let valueToApply = this.applyToAll.inv_pay_amt;
      if ((valueToApply === null || valueToApply === undefined || Number(valueToApply) <= 0) && this.paymentItems.length > 0) {
        // Find first row with a value
        const firstRowWithValue = this.paymentItems.find(item => item.inv_pay_amt !== null && item.inv_pay_amt !== undefined && Number(item.inv_pay_amt) > 0);
        if (firstRowWithValue) {
          valueToApply = Number(firstRowWithValue.inv_pay_amt);
        }
      }
      if (valueToApply !== null && valueToApply !== undefined && Number(valueToApply) > 0) {
        const numValue = Number(valueToApply);
        this.paymentItems.forEach((item) => {
          item.inv_pay_amt = numValue;
        });
      }
    },
    applyDateToAll() {
      // Use value from "Apply to All" section if available, otherwise use first row's value
      let valueToApply = this.applyToAll.inv_pay_date;
      if (!valueToApply && this.paymentItems.length > 0) {
        // Find first row with a value
        const firstRowWithValue = this.paymentItems.find(item => item.inv_pay_date);
        if (firstRowWithValue) {
          valueToApply = firstRowWithValue.inv_pay_date;
        }
      }
      if (valueToApply) {
        this.paymentItems.forEach((item) => {
          item.inv_pay_date = valueToApply;
        });
      }
    },
    applyAllFieldsToAll() {
      this.paymentItems.forEach((item) => {
        if (this.applyFieldCheckboxes.inv_pay_mode && this.applyToAll.inv_pay_mode) {
          item.inv_pay_mode = this.applyToAll.inv_pay_mode;
        }
        if (this.applyFieldCheckboxes.inv_pay_ref_no && this.applyToAll.inv_pay_ref_no) {
          item.inv_pay_ref_no = this.applyToAll.inv_pay_ref_no;
        }
        if (this.applyFieldCheckboxes.inv_pay_amt && this.applyToAll.inv_pay_amt !== null && Number(this.applyToAll.inv_pay_amt) > 0) {
          item.inv_pay_amt = Number(this.applyToAll.inv_pay_amt);
        }
        if (this.applyFieldCheckboxes.inv_pay_date && this.applyToAll.inv_pay_date) {
          item.inv_pay_date = this.applyToAll.inv_pay_date;
        }
      });
    },
    clearPayModeFromAll() {
      this.paymentItems.forEach((item) => {
        item.inv_pay_mode = '';
      });
    },
    clearRefNoFromAll() {
      this.paymentItems.forEach((item) => {
        item.inv_pay_ref_no = '';
      });
    },
    clearAmountFromAll() {
      this.paymentItems.forEach((item) => {
        item.inv_pay_amt = null;
      });
    },
    clearDateFromAll() {
      this.paymentItems.forEach((item) => {
        item.inv_pay_date = '';
      });
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD/MM/YYYY');
    },
    getDocumentUrl(docUrl) {
      if (!docUrl) return '#';
      if (String(docUrl).startsWith('http')) {
        return docUrl;
      }
      return `${URL_BASE_DOC}${docUrl}`;
    },
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },
    resetForm() {
      this.showApplyToAll = false;
      this.applyFieldCheckboxes = {
        inv_pay_mode: false,
        inv_pay_ref_no: false,
        inv_pay_amt: false,
        inv_pay_date: false,
      };
      this.applyToAll = {
        inv_pay_mode: '',
        inv_pay_ref_no: '',
        inv_pay_amt: null,
        inv_pay_date: '',
      };
      this.paymentItems = [];
    },
    submitBulkPayment() {
      if (!this.isFormValid) {
        this.$emit('error', 'Please fill all required fields for all payment items');
        return;
      }

      // Transform payment items back to the format expected by the parent
      const processedItems = this.paymentItems.map((item) => {
        return Object.assign({}, item, {
          inv_pay_mode: item.inv_pay_mode,
          inv_pay_ref_no: item.inv_pay_ref_no,
          inv_pay_amt: Number(item.inv_pay_amt),
          inv_pay_date: item.inv_pay_date,
        });
      });

      this.isSubmitting = true;
      this.$emit('submit', processedItems);
    },
  },
};
</script>

<style scoped>
.table thead th {
  background-color: #f8f9fa;
  font-weight: 600;
}
.table tbody tr:hover {
  background-color: #f5f5f5;
}
</style>

<style>
.modal-xl {
  max-width: 95% !important;
}
</style>




// WEBPACK FOOTER //
// BulkPaymentModal.vue?3c810fde